/**
 * LogDetailModal.jsx — the single-log drill-down.
 *
 * Top half is "what call was this" (ids, route, carriers, status, endpoint);
 * bottom half is the request/response pair side by side, since diffing what we
 * sent against what Freightify answered is the whole reason this screen exists.
 *
 * The rate search is a GET with no body — its payload is the ~28-param query
 * string, logged apart from the URL as `params.toString()`. So the request
 * pane folds that string into a single JSON object (queryParamsToObject) and
 * renders it like any other payload; Raw drops back to the query string
 * exactly as sent. Booking rows have no query string and a real body, so which
 * of the two a row shows follows from the row itself — there's nothing to
 * switch between.
 *
 * Both payloads are stored Mixed and verbatim, which is the other reason
 * Pretty/Raw matters: an upstream failure is often not JSON at all (a gateway
 * HTML error page), and re-indenting would mangle it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { CheckCircle2, Copy, Download, Maximize2, Minimize2, WrapText, X, XCircle } from "lucide-react";
import {
  copyToClipboard,
  downloadTextFile,
  formatCreatedAt,
  formatNumber,
  fullRequestUrl,
  getVariantMeta,
  highlightJson,
  normalizeLog,
  queryParamsToObject,
  formatSno,
  shortId,
  toJsonText,
} from "./logUtils";
import { fetchLogById } from "./oceanLogApi";
import "./OceanApiLogs.css";

const METHOD_CLASS = {
  GET: "bg-blue-50 text-blue-700",
  POST: "bg-green-50 text-green-700",
  PUT: "bg-amber-50 text-amber-700",
  PATCH: "bg-amber-50 text-amber-700",
  DELETE: "bg-red-50 text-red-700",
};

function MetaItem({ label, title, children }) {
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-[0.06em] text-gray-400 uppercase">{label}</p>
      <p className="mt-1 text-[14px] font-medium text-gray-800 break-words" title={title || ""}>
        {children}
      </p>
    </div>
  );
}

function IconAction({ title, onClick, children, tone = "dark" }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        tone === "dark"
          ? "text-slate-400 hover:text-white hover:bg-slate-700"
          : "text-gray-400 hover:text-primary hover:bg-[#FFF4D6]"
      }`}
    >
      {children}
    </button>
  );
}

function PayloadPane({ title, icon, text, wrap, filename, maximized, tone, badge, actions, placeholder }) {
  const handleCopy = async () => {
    const ok = await copyToClipboard(text);
    toast[ok ? "success" : "error"](ok ? `${title} copied` : "Could not copy to clipboard");
  };

  return (
    <div className="flex-1 min-w-0 rounded-xl overflow-hidden border border-slate-800 bg-[#0F172A]">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-800">
        <span className="flex items-center gap-2 text-[13px] font-semibold text-white whitespace-nowrap">
          <span className="text-slate-400">{icon}</span>
          {title}
          {badge}
          {tone === "error" && (
            <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-[11px] font-semibold text-red-400">error</span>
          )}
        </span>
        <span className="flex items-center gap-1">
          {actions}
          <IconAction title={`Copy ${title.toLowerCase()}`} onClick={handleCopy}>
            <Copy size={15} />
          </IconAction>
          <IconAction title={`Download ${title.toLowerCase()}`} onClick={() => downloadTextFile(text, filename)}>
            <Download size={15} />
          </IconAction>
        </span>
      </div>

      <pre
        className={`ocean-log-code m-0 px-4 py-3 overflow-auto text-[12px] leading-[20px] font-mono text-slate-200 ${
          wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre"
        }`}
        style={{ maxHeight: maximized ? "58vh" : "320px" }}
      >
        {placeholder ? (
          <code className="text-slate-500">
            {placeholder === "error"
              ? `Could not load the ${title.toLowerCase()} for this log.`
              : `Loading ${title.toLowerCase()}…`}
          </code>
        ) : text ? (
          <code dangerouslySetInnerHTML={{ __html: highlightJson(text) }} />
        ) : (
          <code className="text-slate-500">No {title.toLowerCase()} recorded for this log.</code>
        )}
      </pre>
    </div>
  );
}

/**
 * Request side. A logged query string IS the request for a rate search, so
 * that's what the pane shows whenever one exists; everything else shows its
 * stored body. No view switch — the row decides.
 */
function RequestPane({ log, paramsObject, paramCount, pretty, wrap, maximized, placeholder }) {
  const bodyText = useMemo(() => toJsonText(log.request?.body ?? log.request, pretty), [log, pretty]);
  // Pretty = the single JSON object; Raw = the query string exactly as sent.
  const paramsText = useMemo(
    () => (pretty ? toJsonText(paramsObject, true) : String(log.params || "")),
    [paramsObject, pretty, log.params]
  );

  const showingParams = paramCount > 0;

  return (
    <PayloadPane
      title={showingParams ? "Request params" : "Request body"}
      icon="&#8599;"
      text={showingParams ? paramsText : bodyText}
      placeholder={showingParams ? null : placeholder}
      wrap={wrap}
      maximized={maximized}
      filename={`ocean-log-${log.id}-request.${showingParams && !pretty ? "txt" : "json"}`}
      badge={
        showingParams && paramCount > 0 ? (
          <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-[11px] font-semibold text-slate-300">
            {paramCount} keys
          </span>
        ) : null
      }
    />
  );
}

function LogDetailModal({ log: listedLog, open, onClose }) {
  const [pretty, setPretty] = useState(true);
  const [wrap, setWrap] = useState(false);
  const [maximized, setMaximized] = useState(false);
  // The listing ships rows without their request/response (they made a page
  // weigh tens of MB), so the payloads are read for the one row being opened.
  const [detail, setDetail] = useState(null);
  const [detailState, setDetailState] = useState("idle"); // idle | loading | error

  const needsPayloads = Boolean(listedLog?.payloads_omitted);

  useEffect(() => {
    if (!open || !listedLog?.id || !needsPayloads) return undefined;

    let cancelled = false;
    setDetail(null);
    setDetailState("loading");
    fetchLogById(listedLog.id)
      .then((row) => {
        if (cancelled) return;
        setDetail(row ? normalizeLog(row) : null);
        setDetailState(row ? "idle" : "error");
      })
      .catch(() => {
        if (!cancelled) setDetailState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [open, listedLog?.id, needsPayloads]);

  // Only the payload fields are taken from the fetched row: the listed one has
  // already been through normalizeGroup, which settles its re-search variant —
  // something a lone row can't know. Lines are taken too when the listing
  // couldn't derive any (a booking whose line sits deep in the payload).
  const log = useMemo(() => {
    if (!listedLog || !detail || detail.id !== listedLog.id) return listedLog;
    const borrowLines = !listedLog.shipping_lines?.length && detail.shipping_lines?.length;
    return {
      ...listedLog,
      request: detail.request,
      response: detail.response,
      params: detail.params,
      error: detail.error,
      ...(borrowLines ? { shipping_lines: detail.shipping_lines, carriers: detail.carriers } : {}),
    };
  }, [listedLog, detail]);

  // "loading" | "error" while the payloads are pending; each pane words its own
  // message from its title ("Loading response…").
  const payloadPlaceholder =
    needsPayloads && log === listedLog ? (detailState === "error" ? "error" : "loading") : null;

  // A fresh log opens in the default view rather than inheriting the toggles
  // left over from the previously inspected row.
  useEffect(() => {
    if (open) {
      setPretty(true);
      setWrap(false);
      setMaximized(false);
    }
  }, [open, log?.id]);

  // `response` is `{ statusCode, body }` on success and `{ statusCode, error }`
  // on failure, so the pane shows whichever half was captured.
  const responsePayload = log?.response?.body ?? log?.response?.error ?? log?.response ?? null;
  const isErrorPayload = log?.response?.body === undefined && log?.response?.error !== undefined;
  const responseText = useMemo(() => toJsonText(responsePayload, pretty), [responsePayload, pretty]);

  const paramsObject = useMemo(() => queryParamsToObject(log?.params), [log?.params]);
  const paramCount = Object.keys(paramsObject).length;

  if (!log) return null;

  // Same tag the table shows — error / re-search variants included.
  const typeMeta = getVariantMeta(log.variant);
  const methodClass = METHOD_CLASS[log.method] || "bg-gray-100 text-gray-700";
  const requestUrl = fullRequestUrl(log);
  const lineNames = log.carriers.length ? log.carriers : log.shipping_lines.map((line) => line.name);

  const handleCopyUrl = async () => {
    const ok = await copyToClipboard(requestUrl);
    toast[ok ? "success" : "error"](ok ? "Request URL copied" : "Could not copy to clipboard");
  };

  const handleCopyId = async () => {
    const ok = await copyToClipboard(log.id);
    toast[ok ? "success" : "error"](ok ? "Log ID copied" : "Could not copy to clipboard");
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={maximized ? "96vw" : 1180}
      bodyStyle={{ padding: 0 }}
      className="ocean-log-modal"
    >
      {/* Amber title bar */}
      <div className="flex items-center justify-between px-6 py-4 bg-primary">
        <span className="flex items-center gap-3">
          <span className="text-[17px] font-semibold text-[#3B2A00]">
            Log details{" "}
            <span className="font-mono font-bold">{formatSno(log.sno)}</span>{" "}
            <button
              type="button"
              onClick={handleCopyId}
              title={`${log.id} — click to copy`}
              className="font-mono font-bold underline decoration-dotted underline-offset-4"
            >
              {shortId(log.id)}
            </button>
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 text-[12px] font-medium text-gray-700">
            <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: typeMeta.dot }} />
            {typeMeta.label}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <button
            type="button"
            title={maximized ? "Restore size" : "Expand"}
            onClick={() => setMaximized((value) => !value)}
            className="p-2 rounded-lg bg-white/25 text-[#3B2A00] hover:bg-white/45"
          >
            {maximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            className="p-2 rounded-lg bg-white/25 text-[#3B2A00] hover:bg-white/45"
          >
            <X size={16} />
          </button>
        </span>
      </div>

      {/* Status strip */}
      <div className="flex items-center gap-2 px-6 py-3.5 bg-[#FAFAFA] border-b border-gray-200">
        {log.success ? (
          <CheckCircle2 size={17} className="text-green-600" />
        ) : (
          <XCircle size={17} className="text-red-500" />
        )}
        <span className={`text-[14px] font-semibold ${log.success ? "text-green-600" : "text-red-500"}`}>
          {log.success ? "Success" : "Failed"}
          {log.status_code ? ` · ${log.status_code}` : ""}
        </span>
        {log.failed && typeof log.error === "string" && (
          <span className="text-[13px] text-gray-500 truncate">— {log.error}</span>
        )}
      </div>

      <div className="max-h-[calc(96vh-140px)] overflow-y-auto">
        {/* Summary grid */}
        <div className="grid grid-cols-3 gap-y-6 gap-x-8 px-6 py-5 border-b border-gray-200 sm:grid-cols-1 md:grid-cols-2">
          <MetaItem label="Query ID" title={log.query_id}>
            <span className="font-mono">
              {formatSno(log.query_sno)} <span className="text-gray-400">{shortId(log.query_id)}</span>
            </span>
          </MetaItem>
          <MetaItem label="Log type">{typeMeta.label}</MetaItem>
          <MetaItem label="Created">{formatCreatedAt(log.created_at)}</MetaItem>

          {/* The account the log belongs to (the query's owner) — and, only when
              they are not the same person, who actually made the call: a KAM
              booking or confirming on a customer's behalf. */}
          <MetaItem label="Requested by" title={log.requested_by_email}>
            {log.requested_by}
            {log.acted_by && log.acted_by !== log.requested_by && (
              <span className="ml-1.5 text-[12px] text-gray-400" title={log.acted_by_email || ""}>
                (called by {log.acted_by})
              </span>
            )}
          </MetaItem>
          <MetaItem label="Route">
            {log.origin || "—"} <span className="text-primary">&#8594;</span> {log.destination || "—"}
          </MetaItem>
          {/* `carriers` is filled from the log's lines when the backend didn't
              send any, so a booking / update / cancel names its booked line
              here too — and the offers count is only a rate-search figure. */}
          <MetaItem label={lineNames.length === 1 ? "Shipping line" : "Shipping lines"} title={lineNames.join(", ")}>
            {lineNames.length ? lineNames.join(", ") : "—"}
            {log.offers_count > 0 && (
              <span className="ml-1.5 text-[12px] font-normal text-gray-400">
                ({formatNumber(log.offers_count)} offers)
              </span>
            )}
          </MetaItem>

          <MetaItem label="Containers">
            {formatNumber(log.total_containers)}
            {log.container_load && (
              <span className="ml-1.5 text-[12px] font-normal text-gray-400">{log.container_load}</span>
            )}
          </MetaItem>
          <MetaItem label="Weight (kg)">{formatNumber(log.total_weight)}</MetaItem>
          <MetaItem label="Method">{log.method || "—"}</MetaItem>
        </div>

        {/* Endpoint — the full URL exactly as called, query string included, so
            it can be read here or copied straight into a replay. The Request
            params pane below is that same query string folded into JSON. */}
        <div className="px-6 py-5 border-b border-gray-200">
          <p className="text-[11px] font-semibold tracking-[0.06em] text-gray-400 uppercase">Endpoint</p>
          <div className="mt-2 flex items-start gap-3 px-4 py-3 rounded-xl bg-[#F7F8FA] border border-gray-200">
            <span className={`shrink-0 px-2 py-1 rounded-md text-[11px] font-bold ${methodClass}`}>
              {log.method || "—"}
            </span>
            <span className="flex-1 min-w-0 text-[13px] leading-[22px] font-mono text-gray-700 break-all">
              {requestUrl || "—"}
            </span>
            {paramCount > 0 && (
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                +{paramCount} params
              </span>
            )}
            <IconAction title="Copy full URL with query string" onClick={handleCopyUrl} tone="light">
              <Copy size={16} />
            </IconAction>
          </div>
        </div>

        {/* Payloads */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold tracking-[0.06em] text-gray-400 uppercase">Payloads</p>
            <div className="flex items-center gap-2">
              <div className="flex items-center p-1 rounded-lg bg-[#F1F3F5]">
                {[
                  { key: true, label: "Pretty" },
                  { key: false, label: "Raw" },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => setPretty(option.key)}
                    className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${
                      pretty === option.key ? "bg-primary text-white" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                title={wrap ? "Disable line wrap" : "Wrap long lines"}
                onClick={() => setWrap((value) => !value)}
                className={`p-2 rounded-lg border transition-colors ${
                  wrap ? "border-primary bg-[#FFF4D6] text-primary" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                <WrapText size={16} />
              </button>
            </div>
          </div>

          <div className="mt-3 flex gap-4 sm:flex-col md:flex-col">
            <RequestPane
              log={log}
              paramsObject={paramsObject}
              paramCount={paramCount}
              pretty={pretty}
              wrap={wrap}
              maximized={maximized}
              placeholder={payloadPlaceholder}
            />
            <PayloadPane
              title="Response"
              icon="&#8601;"
              text={responseText}
              placeholder={payloadPlaceholder}
              wrap={wrap}
              maximized={maximized}
              tone={isErrorPayload ? "error" : "ok"}
              filename={`ocean-log-${log.id}-response.json`}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default LogDetailModal;
